import { Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";

// ── Brand SVG icons ────────────────────────────────────────────────────────────

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0 fill-[#1877F2]" aria-hidden="true">
    <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047v-2.66c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.884v2.267h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/>
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0 fill-[#0A66C2]" aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0 fill-[#25D366]" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const NaverIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0 fill-[#03C75A]" aria-hidden="true">
    <path d="M16.273 12.845L7.376 0H0v24h7.727V11.155L16.624 24H24V0h-7.727z"/>
  </svg>
);

const KakaoIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0 fill-[#3A1D1D]" aria-hidden="true">
    <path d="M12 3C6.477 3 2 6.477 2 10.8c0 2.7 1.554 5.082 3.9 6.51l-.993 3.697 4.283-2.813A11.84 11.84 0 0012 18.6c5.523 0 10-3.477 10-7.8S17.523 3 12 3z"/>
  </svg>
);

// ── Component ─────────────────────────────────────────────────────────────────

interface InsightShareMenuProps {
  url: string;
  title: string;
  thumbnailUrl?: string;
}

const InsightShareMenu = ({ url, title }: InsightShareMenuProps) => {
  const { toast } = useToast();

  const enc = encodeURIComponent;

  const shareLinks = {
    facebook:  `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
    linkedin:  `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`,
    whatsapp:  `https://wa.me/?text=${enc(`${title}\n${url}`)}`,
    naver:     `https://share.naver.com/web/shareView?url=${enc(url)}&title=${enc(title)}`,
  };

  const handleKakao = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast({
        title: "링크가 복사되었습니다 ✓",
        description: "카카오톡을 열고 붙여넣기로 공유하세요.",
      });
    } catch {
      toast({ title: "Copy failed", description: "Please copy the URL manually.", variant: "destructive" });
    }
  };

  const linkClass = "flex w-full cursor-pointer items-center gap-2.5 px-2 py-1.5 text-sm";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          aria-label="Share this article"
          className="h-8 w-8 p-0 text-gray-600 hover:text-primary min-w-[32px]"
        >
          <Share2 className="w-4 h-4 stroke-[1.5]" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52 bg-white p-1">
        <DropdownMenuItem asChild>
          <a href={shareLinks.facebook} target="_blank" rel="noopener noreferrer" className={linkClass}>
            <FacebookIcon />
            <span>Share on Facebook</span>
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a href={shareLinks.linkedin} target="_blank" rel="noopener noreferrer" className={linkClass}>
            <LinkedInIcon />
            <span>Share on LinkedIn</span>
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a href={shareLinks.whatsapp} target="_blank" rel="noopener noreferrer" className={linkClass}>
            <WhatsAppIcon />
            <span>Share on WhatsApp</span>
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a href={shareLinks.naver} target="_blank" rel="noopener noreferrer" className={linkClass}>
            <NaverIcon />
            <span>Share on Naver</span>
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleKakao} className="gap-2.5 cursor-pointer">
          <KakaoIcon />
          <span>Share on KakaoTalk</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default InsightShareMenu;
